import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// ─── Fallback Themes (admin with no company, or pre-login) ────────────────────
const THEMES = {
  blue:   { sidebar: 'linear-gradient(to bottom, #1d4ed8, #1e3a8a)', dot: '#60a5fa', accent: '#93c5fd', label: 'Blue'   },
  teal:   { sidebar: 'linear-gradient(to bottom, #0f766e, #134e4a)', dot: '#14b8a6', accent: '#5eead4', label: 'Teal'   },
  green:  { sidebar: 'linear-gradient(to bottom, #166534, #14532d)', dot: '#4ade80', accent: '#86efac', label: 'Green'  },
  purple: { sidebar: 'linear-gradient(to bottom, #6b21a8, #581c87)', dot: '#a855f7', accent: '#d8b4fe', label: 'Purple' },
  red:    { sidebar: 'linear-gradient(to bottom, #9f1239, #881337)', dot: '#f43f5e', accent: '#fda4af', label: 'Red'    },
  slate:  { sidebar: 'linear-gradient(to bottom, #334155, #1e293b)', dot: '#64748b', accent: '#94a3b8', label: 'Dark'   },
};

// ─── Nav Groups ────────────────────────────────────────────────────────────────
const navGroups = [
  {
    label: 'CORE',
    items: [
      { path: '/',               icon: '📊', label: 'Dashboard',          permKey: 'dashboard' },
      { path: '/visitors',       icon: '👥', label: 'Visitors',           permKey: 'visitors' },
      { path: '/meetings',       icon: '📅', label: 'Meetings',           permKey: 'meetings' },
      { path: '/meeting-rooms',  icon: '🚪', label: 'Meeting Room Setup', managerOnly: true, permKey: 'meeting_rooms' },
    ],
  },
  {
    label: 'PEOPLE',
    items: [
      { path: '/employees', icon: '👔', label: 'Employees', permKey: 'employees' },
      { path: '/drivers',   icon: '🚗', label: 'Drivers',   permKey: 'vehicles' },
    ],
  },
  {
    label: 'ACCOMMODATION',
    items: [
      { path: '/guest-houses',   icon: '🏠', label: 'Guest Houses',   permKey: 'guest_house' },
      { path: '/rooms',          icon: '🏨', label: 'Guest Rooms',    permKey: 'guest_house' },
      { path: '/hotel-bookings', icon: '🏩', label: 'Hotel Bookings', permKey: 'hotels' },
    ],
  },
  {
    label: 'POOL VEHICLES',
    items: [
      { path: '/vehicles', icon: '🚙', label: 'Pool Vehicles', permKey: 'vehicles' },
      { path: '/trips',    icon: '📍', label: 'Vehicle Trips', permKey: 'vehicles' },
      { path: '/fuel',     icon: '⛽', label: 'Fuel Expenses', permKey: 'vehicles' },
    ],
  },
  {
    label: 'EMPLOYEE VEHICLES',
    items: [
      { path: '/employee-vehicles', icon: '🚗', label: 'Employee Vehicles', permKey: 'employee_vehicles' },
      { path: '/showroom-setup',    icon: '🏪', label: 'Showroom Setup',    managerOnly: true, permKey: 'showroom_setup' },
    ],
  },
  {
    label: 'COURIER',
    items: [
      { path: '/courier-companies', icon: '🏢', label: 'Courier Companies', adminOnly: true },
      { path: '/couriers',          icon: '📦', label: 'TCS Courier',       permKey: 'couriers' },
      { path: '/internal-courier',  icon: '🔄', label: 'Internal Courier',  permKey: 'couriers' },
    ],
  },
  {
    label: 'FINANCE',
    items: [
      { path: '/expenses', icon: '💰', label: 'Expenses', permKey: 'expenses' },
    ],
  },
  {
    label: 'ADDRESS BOOK',
    items: [
      { path: '/address-book', icon: '📒', label: 'Address Book', permKey: 'address_book' },
    ],
  },
  {
    label: 'REPORTS & ALERTS',
    items: [
      { path: '/alerts',     icon: '🔔', label: 'Alerts' },
      { path: '/reports',    icon: '📈', label: 'Reports',    permKey: 'reports' },
      { path: '/user-guide', icon: '📖', label: 'User Guide' },
    ],
  },
  {
    label: 'ADMIN',
    items: [
      { path: '/users',             icon: '⚙️',  label: 'User Management',  managerOnly: true, permKey: 'users' },
      { path: '/companies',         icon: '🏢', label: 'Companies',          adminOnly: true },
      { path: '/units',             icon: '🏭', label: 'Units',              adminOnly: true },
      { path: '/backup',            icon: '💾', label: 'Backup & Restore',   adminOnly: true },
      { path: '/admin-help',        icon: '🛠️', label: 'Help & Commands',   adminOnly: true },
      { path: '/access-management', icon: '🔐', label: 'Access Management', adminOnly: true },
      { path: '/drive-backup',      icon: '☁️', label: 'Drive Backup',      adminOnly: true },
    ],
  },
];

// ─── Derive initials from company name ────────────────────────────────────────
function getInitials(name) {
  if (!name) return 'RC';
  return name
    .split(' ')
    .filter(w => w.length > 1)
    .map(w => w[0].toUpperCase())
    .join('')
    .slice(0, 3) || name.slice(0, 2).toUpperCase();
}

// ─── Dynamic Company Logo ──────────────────────────────────────────────────────
// Reads company identity from the authenticated user object (stored in localStorage
// after login). No hardcoded company names — fully driven by DB data via auth.js.
function CompanyLogo({ open, user }) {
  const companyName  = user?.company_name  || 'Reception System';
  const shortName    = user?.company_short_name || getInitials(companyName);
  const logoUrl      = user?.logo_url || null;

  if (open) {
    return (
      <div className="flex items-center gap-3 min-w-0">
        {/* Logo badge */}
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 shadow-inner overflow-hidden">
          {logoUrl
            ? <img src={logoUrl} alt={shortName} className="w-9 h-9 object-contain rounded-xl" onError={e => { e.target.style.display='none'; }} />
            : <span className="text-white font-extrabold text-xs leading-none tracking-tight">{shortName}</span>
          }
        </div>
        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold text-sm leading-tight truncate" title={companyName}>
            {companyName}
          </div>
          <div className="text-white/60 text-xs leading-tight">Reception System</div>
        </div>
      </div>
    );
  }

  // Collapsed state — icon only
  return (
    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shadow-inner mx-auto overflow-hidden">
      {logoUrl
        ? <img src={logoUrl} alt={shortName} className="w-8 h-8 object-contain rounded-xl" onError={e => { e.target.style.display='none'; }} />
        : <span className="text-white font-extrabold text-xs leading-none">{shortName}</span>
      }
    </div>
  );
}

// ─── Theme Picker (only shown when no company branding) ───────────────────────
function ThemePicker({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title="Change theme"
        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors text-sm"
      >
        🎨
      </button>
      {open && (
        <div className="absolute right-0 top-10 bg-white border border-gray-200 rounded-xl shadow-xl p-3 z-50 w-44">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">App Theme</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(THEMES).map(([key, t]) => (
              <button
                key={key}
                onClick={() => { onChange(key); setOpen(false); }}
                title={t.label}
                className="flex flex-col items-center gap-1 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div
                  style={{ background: t.sidebar, width: 28, height: 28, borderRadius: 8,
                           border: current === key ? '2px solid #1d4ed8' : '2px solid transparent' }}
                />
                <span className="text-xs text-gray-600">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────────
export default function Layout() {
  const { user, logout, perms } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('app_theme') || 'blue');
  const [dark, setDark] = useState(() => localStorage.getItem('app_dark') === 'true');

  const handleLogout = () => { logout(); navigate('/login'); };

  const changeTheme = (t) => {
    setTheme(t);
    localStorage.setItem('app_theme', t);
  };

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('app_dark', String(next));
  };

  // ── Company branding logic ──────────────────────────────────────────────────
  // A user "has company branding" when the login response included a company_name.
  // Admin users assigned to a company (migration_v9.sql) also receive branding.
  // Admin users with no company assignment fall back to the manual theme picker.
  const hasCompanyBranding = !!(user?.company_name && user?.color_primary);

  // Sidebar gradient: sidebar_color → color_secondary
  const sidebarBg = hasCompanyBranding
    ? `linear-gradient(to bottom, ${user.sidebar_color || user.color_primary}, ${user.color_secondary || user.color_primary})`
    : (THEMES[theme] || THEMES.blue).sidebar;

  // Accent/dot color for header strip and active nav indicator
  const accentColor = hasCompanyBranding
    ? (user.icon_color || user.color_accent || user.color_primary)
    : (THEMES[theme] || THEMES.blue).dot;

  // Header accent color
  const headerAccent = hasCompanyBranding
    ? (user.header_color || user.color_primary)
    : accentColor;

  return (
    <div className={`flex h-screen overflow-hidden ${dark ? 'bg-gray-900' : 'bg-gray-100'}`}>

      {/* ── Sidebar ── */}
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 text-white flex flex-col shadow-xl flex-shrink-0`}
        style={{ background: sidebarBg }}
      >
        {/* Logo / Company Identity Area */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2 min-h-[64px]">
          <div className="flex-1 min-w-0">
            <CompanyLogo open={sidebarOpen} user={user} />
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white/60 hover:text-white p-1 rounded flex-shrink-0 text-xs"
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navGroups.map((group) => {
            const visibleItems = group.items.filter(item => {
              if (item.adminOnly   && user?.role !== 'admin') return false;
              if (item.managerOnly && !['admin', 'manager'].includes(user?.role)) return false;
              if (item.permKey && user?.role !== 'admin' && perms[item.permKey]?.is_hidden === true) return false;
              return true;
            });
            if (group.adminOnly && user?.role !== 'admin') return null;
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.label}>
                {sidebarOpen && (
                  <div className="px-4 pt-4 pb-1">
                    <p className="text-white/40 text-xs font-semibold tracking-widest uppercase">{group.label}</p>
                  </div>
                )}
                {!sidebarOpen && <div className="mt-2 mx-2 border-t border-white/10" />}
                {visibleItems.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 text-sm sidebar-link ${
                        isActive
                          ? 'active bg-white/20 border-l-4 border-white font-semibold'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                      }`
                    }
                  >
                    <span className="text-base flex-shrink-0">{item.icon}</span>
                    {sidebarOpen && <span className="ml-3 truncate">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {/* User Info at Bottom */}
        <div className="p-4 border-t border-white/10">
          {sidebarOpen ? (
            <div>
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-white/50 text-xs mt-0.5">
                {{ admin: 'Administrator', manager: 'Manager', receptionist: 'Receptionist',
                   boss_secretary: 'Boss Secretary', general_user: 'General User' }[user?.role] || user?.role}
              </p>
              {user?.company_name && (
                <p className="text-white/30 text-xs mt-0.5 truncate">{user.company_name}</p>
              )}
            </div>
          ) : (
            <div className="text-center text-lg" title={user?.full_name}>👤</div>
          )}
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className={`${dark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-3 flex items-center justify-between shadow-sm`}>
          <div className="flex items-center gap-3">
            {/* Accent strip matching company/theme color */}
            <div className="w-1 h-8 rounded-full" style={{ background: headerAccent }} />
            <div>
              <h2 className={`font-semibold text-sm ${dark ? 'text-white' : 'text-gray-800'}`}>
                {user?.company_name ? `${user.company_name}` : 'Reception Management System'}
              </h2>
              <p className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                {new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme Picker — only shown when user has no company branding */}
            {!hasCompanyBranding && <ThemePicker current={theme} onChange={changeTheme} />}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDark}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`p-2 rounded-lg text-sm transition-colors ${dark ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {dark ? '☀️' : '🌙'}
            </button>

            {/* Alerts */}
            <NavLink to="/alerts" className={`p-2 rounded-lg transition-colors ${dark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              🔔
            </NavLink>

            {/* User name */}
            <span className={`text-sm hidden sm:block ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
              👤 {user?.full_name}
            </span>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto p-6 ${dark ? 'bg-gray-900 text-white' : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
